import AccountBalanceSharp from '@mui/icons-material/AccountBalanceSharp';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import Business from '@mui/icons-material/Business';
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined';
import {
  formatDayMonthYear,
  formatRut,
  hasAllOperationalDocuments,
  validEmail,
} from '../index';
import CredentialProviderDialog from '../../components/dialogs/CredentialProviderDialog';
import BankAccountsDialog from '../../components/dialogs/BankAccountsDialog';
import UploadDigitalCertificateDialog from '../../components/dialogs/UploadDigitalCertificateDialog';
import SupplierPortalDialog from '../../components/dialogs/SupplierPortalDialog';
import UploadOperationalDocuments from '../../components/dialogs/UploadOperationalDocuments';
import {
  openCreditBuroFormDialogVar,
  openUploadCreditLineDocumentsDialogVar,
} from '../../apollo/reactive-variables/credit-line';
import NationalTaxServiceCredentialsDialogContent from '../../components/dialogs/NationalTaxServiceCredentialDialogContent';
import hasAllDocuments from '../check-all-documents';
import {
  getDocumentCredentialsForPlatform,
  isDocumentDisabledForPlatform,
  getDocumentDisabledTooltipForPlatform,
} from '../document-platform-config';

const formatInvoiceProviderUsername = (invoiceProvider) => {
  if (invoiceProvider?.authenticationType?.name === 'CERTIFICATE') {
    return {
      title: 'Certificado:',
      value: invoiceProvider?.certificate?.name || 'N/D',
    };
  }
  return {
    title: 'Rut:',
    value: formatRut(invoiceProvider?.username) || 'N/D',
  };
};

export const getPersonalInformation = () => ({
  id: 'personal-information',
  title: 'Información personal',
  isOk: true,
  disabled: true,
  showInProgressBanner: true,
  showInCredentials: false,
});

export const getCurrentCredentialProvider = (
  selectedCompany,
) => selectedCompany?.invoiceProviderCredentials?.at(-1)?.credentialProvider
    || selectedCompany?.possibleCredentialProvider;

export const getCurrentSupplierPortal = (
  selectedCompany,
) => selectedCompany?.supplierPortalCredentials?.at(-1)?.credentialProvider
    || selectedCompany?.possibleCredentialProvider;

export const getErrorMessage = (formData, accounts) => {
  const { number, name, email, bank, accountType } = formData;
  if (name.value === '') {
    return 'El nombre de la cuenta no puede estar vacío';
  }
  if (number.value === '') {
    return 'El número de la cuenta no puede estar vacío';
  }
  if (bank.value === 'choose') {
    return 'Debes elegir algún banco';
  }
  if (accountType.value === 'choose') {
    return 'Debes elegir algún tipo de cuenta';
  }
  if (!validEmail(email.value)) {
    return 'Ingresa un mail válido';
  }
  if (accounts) {
    for (let i = 0; i < accounts.length; i += 1) {
      const account = accounts[i];
      if (account.bankName === bank.value && account.accountNumber === number.value) {
        return 'Esa cuenta ya está registrada';
      }
    }
  }
  return null;
};

export const encryptedBankAccountNumber = (bankAccountNumber) => `**** ${bankAccountNumber.substr(bankAccountNumber.length - 4, 4)}`;

export const getSiiCredentials = (company, nationalTaxService, source) => {
  const hasSiiCredentials = company?.hasSiiCredentials;
  const siiCredentials = company?.siiCredentials || [];
  const currentSiiCredentials = siiCredentials.at(-1);
  const credentials = hasSiiCredentials
    ? [
      {
        key: 'Rut empresa:',
        value: company.masterEntity.displayNationalIdentifier || 'N/D',
      },
      {
        key: 'Actualizado al:',
        value: formatDayMonthYear(currentSiiCredentials.updatedAt),
      },
    ]
    : [];
  return {
    id: 'sii-credentials',
    title: `Credenciales ${nationalTaxService}`,
    subtitle: `Ingresa las credenciales del ${nationalTaxService} de tu empresa de forma segura,
      para sincronizar con Fingo la información de tus facturas y cesiones.`,
    isOk: hasSiiCredentials,
    DialogComponent: NationalTaxServiceCredentialsDialogContent,
    dialogProps: {
      autocomplete: !!company,
      source,
      showTerms: !company?.hasLendingAcceptedterms,
    },
    credentials,
    errorMessage: `Sin credenciales ${nationalTaxService}`,
    showInProgressBanner: true,
    showInCredentials: true,
  };
};

export const getInvoiceProviderCredentials = (company, nationalTaxService, country) => {
  const hasInvoiceProviderCredentials = company?.hasInvoiceProviderCredentials;
  const invoiceProviderCredentials = company?.invoiceProviderCredentials || [];
  const currentInvoiceProviderCredentials = invoiceProviderCredentials.at(-1);
  const credentials = hasInvoiceProviderCredentials
    ? [
      {
        key: 'Facturador:',
        value: currentInvoiceProviderCredentials.credentialProvider.name || 'N/D',
      },
      formatInvoiceProviderUsername(currentInvoiceProviderCredentials),
      {
        key: 'Actualizado al:',
        value: formatDayMonthYear(currentInvoiceProviderCredentials.updatedAt),
      },
    ]
    : [];
  return {
    id: 'invoice-provider',
    title: 'Credenciales del facturador',
    shortTitle: 'Credenciales facturador',
    dialogTitle: 'Ingresa tus credenciales del facturador',
    subtitle:
      'Elige tu facturador y sube tus credenciales para realizar tus cesiones por dentro de la plataforma, y que así no tengas que salir de Fingo.',
    isOk: hasInvoiceProviderCredentials,
    disabled: !company?.hasSiiCredentials && !hasInvoiceProviderCredentials,
    disabledTooltipText: `Primero debes ingresar tus credenciales del ${nationalTaxService}`,
    DialogComponent: CredentialProviderDialog,
    icon: BadgeOutlined,
    credentials,
    errorMessage: 'Sin credenciales del facturador',
    showInProgressBanner: ['Chile'].includes(country?.name),
    showInCredentials: ['Chile'].includes(country?.name),
  };
};

export const getDigitalCertificateCredentials = (company, nationalTaxService, country) => {
  const hasDigitalCertificate = company?.hasDigitalCertificate;
  const currentDigitalCertificate = company?.currentDigitalCertificate;
  const hasExpirationDate = !!currentDigitalCertificate?.expireDatetime;
  const credentials = hasDigitalCertificate
    ? [
      {
        key: 'Certificado:',
        value: currentDigitalCertificate.certificate?.name || 'N/D',
      },
      {
        key: 'Actualizado al:',
        value: formatDayMonthYear(currentDigitalCertificate.updatedAt),
      },
      hasExpirationDate && {
        title: 'Válido hasta el:',
        value: formatDayMonthYear(currentDigitalCertificate.expireDatetime) || 'N/D',
      },
    ]
    : [];
  return {
    id: 'digital-certificate',
    title: 'Certificado digital',
    dialogTitle: 'Administra tu certificado digital',
    subtitle: `Carga aquí el certificado digital del Representante Legal de tu
      empresa para que puedas ceder facturas y contar con más seguridad en las operaciones.`,
    isOk: company?.hasDigitalCertificate,
    disabled: !company?.hasSiiCredentials && !company?.hasDigitalCertificate,
    disabledTooltipText: `Primero debes ingresar tus credenciales del ${nationalTaxService}`,
    DialogComponent: UploadDigitalCertificateDialog,
    icon: TextSnippetOutlined,
    credentials,
    errorMessage: 'Sin certificado digital',
    showInProgressBanner: ['Chile'].includes(country?.name),
    showInCredentials: ['Chile'].includes(country?.name),
  };
};

export const getBankAccountCredentials = (company, nationalTaxService) => {
  const hasBankAccount = company?.hasBankAccount;
  const bankAccounts = company?.bankAccounts || [];
  const currentBankAccountCredentials = bankAccounts.filter((account) => account?.active).at(-1);
  const credentials = hasBankAccount
    ? [
      {
        key: 'Banco:',
        value: currentBankAccountCredentials?.bankName || 'N/D',
      },
      {
        key: 'Nro cuenta:',
        value: currentBankAccountCredentials?.accountNumber || 'N/D',
      },
      {
        key: 'Actualizado al:',
        value: formatDayMonthYear(currentBankAccountCredentials?.updatedAt) || 'N/D',
      },
    ]
    : [];

  return {
    id: 'bank-account',
    title: 'Cuentas bancarias',
    dialogTitle: 'Administra tus cuentas bancarias',
    subtitle: `Ingresa tus datos bancarios para que podamos depositarte. Puedes
      ingresar múltiples cuentas y seleccionar la que más te acomode al momento de girar.`,
    isOk: company?.hasBankAccount,
    disabled: !company?.hasSiiCredentials && !company?.hasBankAccount,
    disabledTooltipText: `Primero debes ingresar tus credenciales del ${nationalTaxService}`,
    DialogComponent: BankAccountsDialog,
    icon: AccountBalanceSharp,
    credentials,
    errorMessage: 'Sin cuentas bancarias',
    showInProgressBanner: true,
    showInCredentials: true,
  };
};

export const getSupplierPortalCredentials = (company, country) => {
  const hasSupplierPortalCredentials = company?.hasSupplierPortalCredentials;
  const supplierPortalCredentials = company?.supplierPortalCredentials || [];
  const currentSupplierPortalCredentials = supplierPortalCredentials.at(-1);
  const credentials = hasSupplierPortalCredentials
    ? [
      {
        key: 'Portal:',
        value: currentSupplierPortalCredentials.credentialProvider.name || 'N/D',
      },
      {
        key: 'Usuario:',
        value: currentSupplierPortalCredentials.secondUsername || 'N/D',
      },
      {
        key: 'Actualizado al:',
        value: formatDayMonthYear(currentSupplierPortalCredentials.updatedAt) || 'N/D',
      },
    ]
    : [];

  return {
    id: 'supplier-portal',
    title: 'Portal de proveedores',
    dialogTitle: 'Ingresa tus credenciales del portal de proveedores',
    subtitle: `Sube las credenciales de tus portales de proveedores para obtener el estado de
      pago de tus facturas al día y descubre todas las nuevas y útiles funcionalidades que tenemos para ti.`,
    isOk: hasSupplierPortalCredentials,
    DialogComponent: SupplierPortalDialog,
    icon: Business,
    credentials,
    errorMessage: 'Sin portal de proveedores',
    showInProgressBanner: false,
    showInCredentials: ['Chile'].includes(country?.name),
  };
};

export const getBuroCreditFormAuthorization = (company, country) => ({
  id: 'buro-credit-authorization-form',
  title: 'Formulario de Autorización para Consulta en Buró de Crédito',
  shortTitle: 'Formulario de Autorización',
  dialogTitle: 'Formulario de Autorización para Consulta en Buró de Crédito',
  isOk: company?.masterEntity?.buroauthorizationformdocumentSet?.length ?? false,
  disabled:
    (!company?.hasSiiCredentials
      || company?.masterEntity?.buroauthorizationformdocumentSet?.length) ?? false,
  disabledTooltipText: !company?.hasSiiCredentials
    ? 'Primero debes ingresar tus credenciales del SAT'
    : null,
  DialogComponent: () => null,
  errorMessage: 'No tienes los formularios ingresados',
  showInProgressBanner: country?.name === 'Mexico',
  showInCredentials: country?.name === 'Mexico',
  onClick: () => openCreditBuroFormDialogVar(true),
});

export const getCreditLineDocumentCredentials = (
  company,
  nationalTaxService,
  country,
  hasAllRequiredDocuments,
) => ({
  id: 'credit-line-documents',
  title: 'Carga de documentos para evaluación',
  shortTitle: 'Documentos requeridos',
  dialogTitle: 'Carga de documentos para evaluación',
  subtitle: null,
  isOk: hasAllRequiredDocuments(company),
  disabled: !company?.hasSiiCredentials,
  disabledTooltipText: `Primero debes ingresar tus credenciales del ${nationalTaxService}`,
  DialogComponent: () => null,
  icon: null,
  credentials: [],
  errorMessage: 'No tienes todos los documentos requeridos',
  showInProgressBanner: ['Mexico'].includes(country?.name),
  showInCredentials: ['Mexico'].includes(country?.name),
  onClick: () => openUploadCreditLineDocumentsDialogVar(true),
});

export const getOperationalDocumentsCredentials = (company, nationalTaxService, country) => ({
  id: 'operational-line-documents',
  title: 'Carga de documentos operacionales',
  shortTitle: 'Documentos operacionales',
  dialogTitle: 'Carga de documentos operacionales',
  subtitle: null,
  isOk: hasAllOperationalDocuments(company),
  disabled: !company?.hasSiiCredentials,
  disabledTooltipText: `Primero debes ingresar tus credenciales del ${nationalTaxService}`,
  DialogComponent: UploadOperationalDocuments,
  icon: null,
  credentials: [],
  errorMessage: 'No tienes todos los documentos requeridos',
  showInProgressBanner: false,
  showInCredentials: ['Mexico'].includes(country?.name),
});

export const getDocumentCredentials = (company, document, requestingPlatform = null) => {
  const platformCredentials = getDocumentCredentialsForPlatform(
    requestingPlatform,
    document.code,
    company,
  );

  let isOk = hasAllDocuments(company?.documents || [], [document]);
  let disabled = false;
  let credentials = [
    {
      key: 'Actualizado al:',
      value: formatDayMonthYear(company?.documents?.filter((
        doc,
      ) => doc.documentType === document.code)?.at(-1)?.updatedAt) || 'N/D',
    },
  ];

  if (platformCredentials) {
    const hasFrameworkContract = company?.hasFrameworkContract;
    isOk = !!hasFrameworkContract;
    disabled = isDocumentDisabledForPlatform(requestingPlatform, document.code);
    credentials = platformCredentials;
  }

  return {
    id: document.code,
    title: document.name,
    shortTitle: document.name,
    dialogTitle: `Carga de ${document.name}`,
    subtitle: null,
    isOk,
    disabled,
    disabledTooltipText: getDocumentDisabledTooltipForPlatform(
      requestingPlatform,
      document.code,
    ) || (disabled ? 'El documento no está disponible para descarga' : null),
    DialogComponent: null,
    icon: null,
    credentials,
    errorMessage: 'Sin documento subido',
    showInProgressBanner: false,
    showInCredentials: true,
  };
};
